/**
 * Chat Discoverability Overlay
 *
 * Onboarding overlay that introduces users to the AI Note Assistant chat sidebar.
 * Shows a blur effect over the main content area with a centered message and
 * large animated curved arrow pointing to the chat sidebar.
 *
 * Features:
 * - Absolute positioned overlay within NoteChatLayout
 * - Automatically inherits Container centering on ultrawide screens
 * - Blur backdrop covering main content (excludes chat sidebar and top nav bar)
 * - Centered heading with call-to-action text below
 * - Hand-drawn curved arrow pointing to chat sidebar
 * - Manual dismiss by clicking anywhere in the document
 * - Auto-dismiss after 10 seconds
 * - Auto-dismiss on window resize to narrow screen
 * - Only renders on screens >= 1280px (lg breakpoint)
 */

import React, { useEffect } from 'react';
import { Box, keyframes, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { LAYOUT_DIMENSIONS } from '../constants/layout';

export interface ChatDiscoverabilityOverlayProps {
  /**
   * Callback fired when overlay is dismissed
   * Called on manual dismiss (X button) or auto-dismiss (timer)
   * @param method - How the overlay was dismissed: 'manual' | 'auto'
   */
  onDismiss: (method: 'manual' | 'auto') => void;

  /**
   * Duration in milliseconds before auto-dismiss
   * @default 9000
   */
  autoDismissDelay?: number;
}

/**
 * Keyframe animation for fade-in entrance
 */
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

/**
 * SVG curved arrow component
 * Hand-drawn style arrow pointing to the chat sidebar
 */
const CurvedArrow: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="200"
    height="120"
    viewBox="0 0 55 35"
    role="img"
    aria-label="Arrow pointing to chat sidebar"
  >
    <g
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeMiterlimit="10"
    >
      <path d="M48.569 27.616c-4.254-4.052-15.443 2.992-20.225 3.767-18.011 2.916-23.035-13.832-27.109-28.1" />
      <path
        strokeLinejoin="round"
        d="M41.907 31.488c3.811-2.175 7.875-.701 11.857-2.026-2.17-2.355-6.355-7.329-6.883-10.66"
      />
    </g>
  </svg>
);

/**
 * ChatDiscoverabilityOverlay
 *
 * Renders a dismissable overlay that introduces the AI chat sidebar to new users.
 * Positioned absolutely within NoteChatLayout to inherit Container centering on ultrawide screens.
 *
 * @example
 * ```tsx
 * // In NoteChatLayout
 * <NoteChatLayout
 *   overlay={isChatOnboardingVisible && (
 *     <ChatDiscoverabilityOverlay
 *       onDismiss={() => setShowOnboarding(false)}
 *     />
 *   )}
 *   // ... other props
 * />
 * ```
 */
export const ChatDiscoverabilityOverlay: React.FC<ChatDiscoverabilityOverlayProps> = ({
  onDismiss,
  autoDismissDelay = 9000,
}) => {
  const theme = useTheme();
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('lg'));

  // Auto-dismiss after specified delay
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss('auto');
    }, autoDismissDelay);

    return () => clearTimeout(timer);
  }, [onDismiss, autoDismissDelay]);

  // Auto-dismiss when screen becomes narrow (chat sidebar would be hidden)
  useEffect(() => {
    if (isNarrowScreen) {
      onDismiss('auto');
    }
  }, [isNarrowScreen, onDismiss]);

  // Document-level click handler - dismiss on any click
  useEffect(() => {
    const handleDocumentClick = () => {
      onDismiss('manual');
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [onDismiss]);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: LAYOUT_DIMENSIONS.CHAT_SIDEBAR_WIDTH + 1,
        bottom: 0,
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        zIndex: theme.zIndex.appBar - 1,
        animation: `${fadeIn} 300ms ease-in-out`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Content - Repositioned to match annotation */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          width: '95%',
        }}
      >
        {/* Heading */}
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 600,
            color: theme.palette.primary.main,
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          Meet your AI Note Assistant
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            textAlign: 'center',
            maxWidth: '600px',
            lineHeight: 1.6,
          }}
        >
          Shape your notes using simple, natural language — whether you&apos;re updating a single
          section or refining the entire note.
        </Typography>

        {/* Arrow and text container - positioned lower */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 250,
            mt: 4,
          }}
        >
          {/* "Try it out here" text - positioned on the left */}
          <Typography
            variant="h6"
            sx={{
              position: 'absolute',
              top: 80,
              right: 175,
              fontStyle: 'italic',
              color: theme.palette.grey[800],
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
              fontWeight: 500,
            }}
          >
            Try it out here
          </Typography>

          {/* Curved arrow pointing to chat sidebar */}
          <Box
            sx={{
              position: 'absolute',
              top: 120,
              right: 0,
              color: theme.palette.primary.main,
            }}
          >
            <CurvedArrow />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
